import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function StudioConsultingPage() {
  useSEO({
    title: '스튜디오 컨설팅 - KR STUDIO | 맞춤형 스튜디오 구축',
    description: 'KR스튜디오의 전문 컨설팅으로 최적의 스튜디오를 구축하세요. 공간 설계, 장비 선정, 운영 노하우까지 토탈 솔루션을 제공합니다.',
    keywords: '스튜디오 컨설팅, 스튜디오 구축, 장비 컨설팅, 공간 설계, 스튜디오 운영',
    canonical: '/studio-consulting',
    schema: createServiceSchema(
      '스튜디오 컨설팅 서비스',
      '공간 설계, 장비 선정, 운영 노하우까지 토탈 스튜디오 구축 솔루션'
    ),
  });

  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState(0);

  const packages = [
    {
      tier: '🥉 베이직',
      price: '3천만원~',
      target: '소규모 1인 스튜디오',
      features: [
        '면적: 10평',
        '기본 방음/조명',
        '1인 촬영 세트',
        '기초 장비 구성'
      ],
      color: 'from-amber-600 to-amber-700'
    },
    {
      tier: '🥈 스탠다드',
      price: '7천만원~',
      target: '중형 다목적 스튜디오',
      features: [
        '면적: 20평',
        '전문 방음 시공',
        '크로마키 + 세트',
        '다목적 촬영 가능'
      ],
      color: 'from-gray-400 to-gray-600'
    },
    {
      tier: '🥇 프리미엄',
      price: '1.5억~',
      target: '대형 통합 스튜디오',
      features: [
        '면적: 30평+',
        '완벽 방음/차음',
        '멀티 세트 구성',
        '라이브 송출 시스템'
      ],
      color: 'from-yellow-500 to-yellow-600'
    }
  ];

  const process = [
    {
      phase: '1단계',
      title: '현장 진단',
      duration: '무료',
      icon: 'ri-ruler-2-line',
      tasks: [
        '공간 실측 및 분석',
        '콘텐츠 목적 파악',
        '예산 범위 협의',
        '기초 제안서 제공'
      ],
      color: 'bg-blue-500'
    },
    {
      phase: '2단계',
      title: '설계',
      duration: '2주',
      icon: 'ri-pencil-ruler-2-line',
      tasks: [
        '3D 도면 제작',
        '장비 리스트 확정',
        '시공 일정 수립',
        '최종 견적 확정'
      ],
      color: 'bg-purple-500'
    },
    {
      phase: '3단계',
      title: '시공',
      duration: '4주',
      icon: 'ri-hammer-line',
      tasks: [
        '방음/전기 공사',
        '장비 설치',
        '시스템 연동',
        '테스트 촬영'
      ],
      color: 'bg-orange-500'
    },
    {
      phase: '4단계',
      title: '교육',
      duration: '1주',
      icon: 'ri-graduation-cap-line',
      tasks: [
        '장비 사용법',
        '촬영 기법',
        '편집 워크플로우',
        '문제 해결법'
      ],
      color: 'bg-green-500'
    },
    {
      phase: '5단계',
      title: '사후 관리',
      duration: '지속',
      icon: 'ri-customer-service-2-line',
      tasks: [
        '3개월 무상 AS',
        '정기 점검',
        '업그레이드 컨설팅',
        '긴급 기술 지원'
      ],
      color: 'bg-teal-500'
    }
  ];

  const portfolios = [
    {
      client: '서울대학교 교수학습센터',
      image: 'https://public.readdy.ai/ai/img_res/1aff73b67bd91aacec00d6283f5bd507.jpg',
      cost: '8천만원',
      duration: '5주',
      result: '월 제작량: 50편 → 200편',
      stats: [
        { label: '구축 비용', value: '8천만원' },
        { label: '구축 기간', value: '5주' },
        { label: '제작량 증가', value: '4배' }
      ]
    },
    {
      client: '삼성전자 사내 스튜디오',
      image: 'https://readdy.ai/api/search-image?query=Corporate%20in-house%20professional%20video%20studio%20with%20multiple%20camera%20setup%2C%20branded%20backdrop%2C%20executive%20interview%20area%2C%20modern%20business%20environment%2C%20high-end%20production%20equipment%2C%20clean%20professional%20design&width=800&height=600&seq=scp2&orientation=landscape',
      cost: '2억원',
      duration: '8주',
      result: 'ROI: 6개월 내 회수',
      stats: [
        { label: '구축 비용', value: '2억원' },
        { label: '구축 기간', value: '8주' },
        { label: 'ROI', value: '6개월' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section - Before/After Studio Transformation */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20studio%20transformation%20before%20and%20after%20comparison%2C%20modern%20video%20production%20facility%20construction%20progress%2C%20empty%20room%20to%20fully%20equipped%20studio%2C%20dramatic%20improvement%2C%20clean%20professional%20workspace&width=1920&height=800&seq=schero2&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-purple-500/20 border border-purple-400 px-6 py-2 rounded-full mb-8">
              <span className="text-purple-300 font-bold">🏗️ 스튜디오 컨설팅</span>
            </div>
            <h1 className="text-6xl font-black mb-6 leading-tight">
              콘텐츠 제작자가 만드는<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">진짜 스튜디오</span>
            </h1>
            <p className="text-2xl mb-12 text-gray-300">
              인테리어가 아닌, <strong className="text-white">시스템을 구축</strong>합니다
            </p>
            <div className="flex gap-4 justify-center hidden">
              <a href="mailto:contact@krstudio.com" className="bg-purple-500 text-white px-10 py-4 text-lg font-bold rounded-full hover:bg-purple-600 transition cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-105 transform duration-300">
                무료 컨설팅
              </a>
              <a href="#diagnosis" className="border-2 border-white text-white px-10 py-4 text-lg font-bold rounded-full hover:bg-white hover:text-gray-900 transition cursor-pointer whitespace-nowrap">
                무료 현장 진단 신청
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-4">
              🏗️ 왜 KR 컨설팅인가?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Regular Interior Company */}
            <div className="bg-gray-50 rounded-3xl p-10 border-2 border-gray-200">
              <h3 className="text-2xl font-black text-gray-400 mb-8 text-center">일반 인테리어 업체</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-red-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-600 text-lg">예쁜 공간 디자인</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-red-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-600 text-lg">장비만 설치</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-red-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-600 text-lg">운영 방법 모름</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-red-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-600 text-lg">사후 지원 없음</p>
                  </div>
                </div>
              </div>
            </div>

            {/* KR Studio Consulting */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-10 border-2 border-purple-300 shadow-xl relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <div className="bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  BEST
                </div>
              </div>
              <h3 className="text-2xl font-black text-purple-900 mb-8 text-center">KR스튜디오 컨설팅</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 text-lg font-bold">콘텐츠 중심 설계</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 text-lg font-bold">장비 + 워크플로우</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 text-lg font-bold">운영 교육 제공</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 text-lg font-bold">지속 기술 지원</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-4">구축 패키지</h2>
            <p className="text-xl text-gray-600">규모와 목적에 맞는 맞춤형 솔루션</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 ${selectedPackage === index ? 'border-purple-500' : 'border-gray-200'}`}
                onClick={() => setSelectedPackage(index)}
              >
                <div className={`bg-gradient-to-r ${pkg.color} text-white p-8 text-center`}>
                  <div className="text-4xl mb-3">{pkg.tier.split(' ')[0]}</div>
                  <h3 className="text-2xl font-black mb-2">{pkg.tier.split(' ')[1]}</h3>
                  <div className="text-3xl font-black">{pkg.price}</div>
                </div>
                <div className="p-8">
                  <p className="text-center text-lg font-bold text-gray-700 mb-6">{pkg.target}</p>
                  <ul className="space-y-4">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-purple-600 text-sm"></i>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a 
                    href="mailto:contact@krstudio.com"
                    className="block mt-8 bg-gray-900 text-white text-center py-4 rounded-full font-bold hover:bg-purple-600 transition cursor-pointer whitespace-nowrap"
                  >
                    견적 문의
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-4">컨설팅 프로세스</h2>
            <p className="text-xl text-gray-600">체계적인 5단계 구축 시스템</p>
          </div>

          <div className="max-w-6xl mx-auto space-y-6">
            {[
              {
                step: '1단계',
                title: '현장 진단',
                duration: '무료',
                items: ['공간 실측 및 분석', '콘텐츠 목적 파악', '예산 범위 협의', '기초 제안서 제공']
              },
              {
                step: '2단계',
                title: '설계',
                duration: '2주',
                items: ['3D 도면 제작', '장비 리스트 확정', '시공 일정 수립', '최종 견적 확정']
              },
              {
                step: '3단계',
                title: '시공',
                duration: '4주',
                items: ['방음/전기 공사', '장비 설치', '시스템 연동', '테스트 촬영']
              },
              {
                step: '4단계',
                title: '교육',
                duration: '1주',
                items: ['장비 사용법', '촬영 기법', '편집 워크플로우', '문제 해결법']
              },
              {
                step: '5단계',
                title: '사후 관리',
                duration: '지속',
                items: ['3개월 무상 AS', '정기 점검', '업그레이드 컨설팅', '긴급 기술 지원']
              }
            ].map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-white font-black text-xl">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="text-2xl font-black text-gray-900">{process.title}</h3>
                      <span className="px-4 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-bold">
                        {process.duration}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {process.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <i className="ri-checkbox-circle-fill text-secondary text-lg"></i>
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Gallery Section */}
      <section id="diagnosis" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-4">구축 사례</h2>
            <p className="text-xl text-gray-600">검증된 성공 스토리</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {portfolios.map((portfolio, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img 
                    src={portfolio.image}
                    alt={portfolio.client}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-purple-500 text-white px-4 py-2 rounded-full font-bold">
                    성공 사례
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-black text-gray-900 mb-6">{portfolio.client}</h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {portfolio.stats.map((stat, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-sm text-gray-500 mb-1">{stat.label}</div>
                        <div className="text-lg font-black text-purple-600">{stat.value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200">
                    <div className="flex items-center space-x-3">
                      <i className="ri-line-chart-line text-3xl text-purple-600"></i>
                      <div>
                        <div className="text-sm text-gray-600 mb-1">성과</div>
                        <div className="text-xl font-black text-gray-900">{portfolio.result}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-purple-900 via-purple-800 to-gray-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block bg-purple-500/20 border border-purple-400 px-6 py-2 rounded-full mb-8">
              <span className="text-purple-300 font-bold">🎯 무료 컨설팅</span>
            </div>
            <h2 className="text-5xl font-black mb-6">
              진짜 스튜디오를<br />
              만들 준비가 되셨나요?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              콘텐츠 제작 전문가가 직접 설계하고 구축하는<br />
              시스템 중심의 스튜디오를 경험하세요
            </p>
            <div className="flex gap-4 justify-center">
              <a href="mailto:contact@krstudio.com" className="inline-block bg-white text-purple-900 px-12 py-5 text-lg font-bold rounded-full hover:bg-gray-100 transition cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-105 transform duration-300 hidden">
                무료 현장 진단 신청
              </a>
              <a href="tel:02-1234-5678" className="inline-block border-2 border-white text-white px-12 py-5 text-lg font-bold rounded-full hover:bg-white hover:text-purple-900 transition cursor-pointer whitespace-nowrap hidden">
                전화 상담
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}
